import React from "react";
import { Form } from "react-final-form";
import { useHistory, useParams } from "react-router-dom";
import { useLocation } from "react-router";
import { queryCache, useQuery } from "react-query";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Container from "@material-ui/core/Container";
import { Divider } from "@material-ui/core";
import Loading from "../../../components/Loading";
import Button from "../../../components/Button";
import { useApplicationConfig } from "../../application/modals/hooks/useApplicationConfig";
import { useWizard } from "../../wizard/context";
import { useAuth } from "../../auth/hooks/useAuth";
import { UserRole } from "../../user/enums/UserRole";
import { Routes } from "../../../constants/routes";
import { urlReplacer } from "../../../utils";
import { Helmet } from "react-helmet";
import { endOfTitle } from "../../../constants/constants";
import Error from "../../../components/Error";
import useStudentQueries from "../../student/hooks/useStudentQueries";
import useLanguageCourseQueries from "../hooks/useLanguageCourseQueries";
import { SchoolInfoWidget } from "../../program/components/ViewMorePage/SchoolInfoWidget";
import { RequiredDocumentsWidget } from "../../program/components/ViewMorePage/RequiredDocumentsWidget";
import { FeesWidget } from "../../program/components/ViewMorePage/FeesWidget";
import { BranchesWidget } from "../../program/components/ViewMorePage/BranchesWidget";
import { RestrictedCountriesWidget } from "../../program/components/ViewMorePage/RestrictedCountriesWidget";
import { ViewMoreDetailsWidget } from "../../program/components/ViewMorePage/ViewMoreDetailsWidget";
import { ExtraDocumentsWidget } from "../../program/components/ViewMorePage/ExtraDocumentsWidget";
import { parseToLanguageCourseViewMoreFrontEndModel } from "../../program/helpers/parseToLanguageCourseViewMoreFrontEndModel";
import { EducationType } from "../../school/constants/types";
import { LanguageCourseSubInfoWidget } from "../components/LanguageCourseSubInfoWidget";
import { CourseDetailType } from "../types";

const useStyles = makeStyles(() => ({
  main: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    width: 1144,
    border: "1px solid #e8eaf0",
    background: "#FFFFFF",
    boxShadow: "0px 0px 8px #C2C9D6",
    borderRadius: 15,
  },
  content: {
    padding: "16px 32px",
  },
  divider: {
    backgroundColor: "#DBDBDB",
  },
  subContainer: {
    marginTop: 42,
  },
  extraDocumentsContainer: {
    marginTop: 56,
  },
  applyButtonContainer: {
    marginTop: 48,
    marginBottom: 42,
    display: "flex",
    justifyContent: "right",
  },
  applyButton: {
    width: 235,
    height: 44,
  },
}));

export const LanguageCourseViewMorePage = () => {
  const classes = useStyles();

  const history = useHistory();
  const { pathname } = useLocation();
  const params: { id: string } = useParams();
  const { fetchViewMore } = useLanguageCourseQueries();
  const { fetchCredits } = useStudentQueries();
  const { openWizard } = useWizard();
  const { getCreateApplicationConfig, getNoCreditsConfig } =
    useApplicationConfig();
  const { user } = useAuth();
  const isStudent = user.role === UserRole.Student;
  const isParent = user.role === UserRole.Parent;
  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);

  const { isLoading, data } = useQuery<any>(`${params.id}`, fetchViewMore);

  if (isLoading) {
    return <Loading />;
  }

  const applyAs = async () => {
    if (isStudent || isParent) {
      const {
        data: { availableCredits, package: currentPackage },
      } = await fetchCredits();

      const hasPackage = currentPackage !== null;
      const hasCredits = availableCredits > 0;

      if (!hasPackage || !hasCredits) {
        if (await openWizard(getNoCreditsConfig(hasPackage))) {
          history.push(
            `/${Routes.EXTRA_SERVICES}?pack=2&redirectUrl=${pathname}`,
          );
        }

        return;
      }
    }

    const result = await openWizard(
      getCreateApplicationConfig(params.id, EducationType.Language),
    );

    if (result) {
      await queryCache.invalidateQueries();
      const applicationRoute = isStudent
        ? `/0/${Routes.APPLICATIONS}`
        : urlReplacer(`/0/${Routes.STUDENT}/${Routes.APPLICATIONS}`, {
            id: result.studentId,
          });

      history.push(applicationRoute);
    }
  };

  const course = data
    ? parseToLanguageCourseViewMoreFrontEndModel(data.data)
    : null;

  return (
    <Container maxWidth="lg" className={classes.main}>
      <Helmet>
        <title>View More{endOfTitle}</title>
      </Helmet>
      {!course ? (
        <Error text={"The course was not found"} />
      ) : (
        <div className={classes.root}>
          <SchoolInfoWidget
            school={course.school}
            isMasked={course.isMasked}
            row0={{
              text0: course.school.name,
            }}
            row1={{
              text0: course.language,
            }}
            row2={{
              text1: "Category - ",
              subText1: course.category,
            }}
          />
          <Divider className={classes.divider} />
          <div className={classes.content}>
            <LanguageCourseSubInfoWidget
              headquarterCountry={course.school.country}
              programType={course.courseType}
              programDuration={course.duration}
            />
            <div className={classes.subContainer}>
              <RequiredDocumentsWidget
                requiredDocs={course.requiredDocs}
              />
            </div>
            <div className={classes.subContainer}>
              <Form
                onSubmit={() => undefined}
                render={() => (
                  <FeesWidget fees={course.fees} isMasked={course.isMasked} />
                )}
              />
            </div>
            <div className={classes.subContainer}>
              <BranchesWidget branches={course.branches} />
            </div>
            {!!course.restrictedCountries?.length && (
              <div className={classes.subContainer}>
                <RestrictedCountriesWidget
                  restrictedCountries={course.restrictedCountries}
                />
              </div>
            )}
            <div className={classes.subContainer}>
              <ViewMoreDetailsWidget
                programDetails={course.details}
                isMasked={course.isMasked}
                notMaskedFields={[
                  CourseDetailType.PRICES,
                  CourseDetailType.OTHER_FEES,
                  CourseDetailType.DEPOSIT_AND_REFUND_POLICY,
                ]}
              />
            </div>
            {!!course.extraDocuments.length && (
              <div className={classes.extraDocumentsContainer}>
                <ExtraDocumentsWidget extraDocuments={course.extraDocuments} />
              </div>
            )}
            <div className={classes.applyButtonContainer}>
              <Button
                className={classes.applyButton}
                type={"submit"}
                disabled={isAdminOrSupport}
                variant={"contained"}
                color={"secondary"}
                fullWidth
                onClick={applyAs}
              >
                {isStudent ? "Apply" : "Apply As"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};
